/**
 * ============================================================================
 * ErrorLog 错误日志系统模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 ErrorLog 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createErrorLogApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createErrorLogApi } from '@bayes-mes/shared/api/error-log/factory'
 * export const errorLogApi = createErrorLogApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await errorLogApi.getErrorLogList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createErrorLogApi } from '@bayes-mes/shared/api/error-log/factory'
 * export const errorLogApi = createErrorLogApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/error-log/factory
 * @description ErrorLog 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-20
 * @last-updated 2025-10-20
 * 
 * @change-log
 * - 2025-10-20: 初始创建，实现所有错误日志管理方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  ErrorLog,                        // 错误日志主实体类型
  ErrorLogQueryParams,              // 列表查询参数
  ErrorLogStatisticsQueryParams,    // 统计查询参数
  ErrorLogTrendQueryParams,         // 趋势查询参数
  ResolveErrorForm,                 // 标记解决表单
  BatchResolveErrorForm,            // 批量标记解决表单
  BatchDeleteErrorForm,             // 批量删除表单
  CleanupErrorLogsForm,             // 清理旧日志表单
  ErrorLogStatistics,               // 统计数据
  ErrorLogTrend,                    // 趋势数据
  ErrorLogListResponse,             // 列表响应
} from '../../types/error-log'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 ErrorLog 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 ErrorLog 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 ErrorLog 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createErrorLogApi(client: IRequestClient) {
  return {
    // ===== 查询操作 =====
    
    /**
     * 获取错误日志列表（分页）
     * 
     * 使用场景：
     * - 在错误日志管理页面的主列表中使用
     * - 支持分页、搜索、多维度筛选功能
     * - 系统管理员可以根据错误级别、时间范围、解决状态等条件查询
     * - 用于监控系统错误情况，快速定位问题
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页）
     *   - per_page?: number - 每页数量，默认 20，最大 100
     *     示例：per_page: 20（每页20条）
     *   - level?: ErrorLevel - 错误级别筛选
     *     示例：level: ErrorLevel.ERROR（只显示错误级别）
     *   - type?: string - 异常类名筛选（模糊搜索）
     *     示例：type: 'NotFound'（搜索404错误）
     *   - message?: string - 错误消息筛选（模糊搜索）
     *     示例：message: 'database'（搜索数据库相关错误）
     *   - is_resolved?: boolean - 是否已解决
     *     示例：is_resolved: false（只显示未解决的错误）
     *   - user_id?: number - 按用户筛选
     *   - environment?: string - 按运行环境筛选（local、production等）
     *   - start_date?: string - 开始日期（YYYY-MM-DD）
     *   - end_date?: string - 结束日期（YYYY-MM-DD）
     *   - sort_by?: string - 排序字段，默认：created_at
     *   - sort_order?: 'asc' | 'desc' - 排序方向，默认：desc
     * 
     * @returns Promise<ErrorLogListResponse> 分页结果对象
     *   返回的数据结构：
     *   {
     *     items: ErrorLog[],       // 错误日志列表数组
     *     total: number,           // 总记录数，如 158
     *     page: number,            // 当前页码
     *     per_page: number,        // 每页数量
     *     totalPages: number,      // 总页数
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：管理员在错误日志管理页面选择筛选条件（如：只看未解决的ERROR级别错误）
     * 2. 前端调用：errorLogApi.getErrorLogList({ level: 'error', is_resolved: false, page: 1 })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/error-logs?level=error&is_resolved=false&page=1
     * 5. 后端处理：
     *    - 路由匹配到 ErrorLogController@index 方法
     *    - 验证查询参数（ErrorLogQueryParams）
     *    - 查询 mes_system_error_logs 表，应用筛选条件和分页
     *    - 通过 ErrorLogResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 ErrorLogListResponse 类型的数据
     * 9. 页面更新：使用数据更新表格显示，管理员可以查看错误详情、标记解决等
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取所有未解决的错误（使用默认分页）
     * const unresolved = await errorLogApi.getErrorLogList({
     *   is_resolved: false
     * })
     * 
     * // 示例2：获取今天的ERROR级别错误
     * const todayErrors = await errorLogApi.getErrorLogList({
     *   level: ErrorLevel.ERROR,
     *   start_date: '2025-10-20',
     *   end_date: '2025-10-20'
     * })
     * 
     * // 示例3：搜索特定异常类型
     * const notFoundErrors = await errorLogApi.getErrorLogList({
     *   type: 'NotFoundHttpException',
     *   page: 1,
     *   per_page: 50
     * })
     * 
     * // 示例4：查看生产环境的严重错误
     * const criticalErrors = await errorLogApi.getErrorLogList({
     *   level: ErrorLevel.CRITICAL,
     *   environment: 'production',
     *   sort_by: 'occurrence_count',  // 按出现次数排序
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getErrorLogList(params?: ErrorLogQueryParams) {
      return client.get<ErrorLogListResponse>(API_ROUTES.errorLogs.list, { params })
    },
    
    /**
     * 获取错误日志详情
     * 
     * 使用场景：
     * - 在错误日志详情页面显示完整信息
     * - 查看错误的堆栈跟踪、请求数据、上下文信息
     * - 管理员分析和调试错误时使用
     * - 标记错误为已解决前查看详细信息
     * 
     * @param id - 错误日志的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的错误日志）
     * 
     * @returns Promise<ErrorLog> 单个错误日志对象（包含完整信息）
     *   返回的数据结构：
     *   {
     *     id: number,                // 错误日志 ID
     *     level: ErrorLevel,         // 错误级别
     *     type: string,              // 异常类名
     *     message: string,           // 错误消息
     *     file: string,              // 错误文件路径
     *     line: number,              // 错误行号
     *     trace: string,             // 完整堆栈跟踪（⚠️ 详情页会自动请求）
     *     context: object,           // 上下文信息（PHP版本、内存使用等）
     *     requestData: object,       // 请求数据（敏感信息已脱敏）
     *     url: string,               // 请求URL
     *     method: string,            // 请求方法
     *     userId: number,            // 用户ID
     *     username: string,          // 用户名
     *     ipAddress: string,         // IP地址
     *     environment: string,       // 运行环境
     *     occurrenceCount: number,   // 出现次数
     *     firstOccurredAt: string,   // 首次出现时间
     *     lastOccurredAt: string,    // 最后出现时间
     *     isResolved: boolean,       // 是否已解决
     *     resolvedAt: string,        // 解决时间
     *     resolvedBy: string,        // 解决人
     *     resolutionNote: string,    // 解决说明
     *     createdAt: string,         // 创建时间（ISO 8601格式）
     *     updatedAt: string          // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：errorLogApi.getErrorLogDetail(123)
     *    - 注意：详情页自动请求包含堆栈跟踪等完整信息
     * 3. 发送请求：GET /api/v1/error-logs/123?include_trace=true&include_request=true&include_user_agent=true
     * 4. 后端处理：ErrorLogController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 根据请求参数决定是否包含堆栈跟踪、请求数据等
     *    - 通过 ErrorLogResource 格式化数据
     * 5. 返回数据：单个 ErrorLog 对象（包含完整堆栈跟踪）
     * 6. 前端显示：在详情页展示错误信息，管理员可以分析问题并标记解决
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的错误日志详情
     * const detail = await errorLogApi.getErrorLogDetail(123)
     * console.log(detail.message)  // 错误消息
     * console.log(detail.trace)    // 完整堆栈跟踪
     * console.log(detail.occurrenceCount)  // 出现次数
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const errorLog = await errorLogApi.getErrorLogDetail(Number(id))
     * 
     * // 显示错误信息
     * if (errorLog.isResolved) {
     *   console.log('已解决，解决人：', errorLog.resolvedBy)
     *   console.log('解决说明：', errorLog.resolutionNote)
     * }
     * ```
     * 
     * 错误处理：
     * - 404：错误日志不存在
     * - 403：无权限访问该错误日志
     * - 422：ID 格式错误（不是数字）
     */
    getErrorLogDetail(id: number) {
      // 详情页默认请求包含完整信息（堆栈跟踪、请求数据、User Agent）
      return client.get<ErrorLog>(API_ROUTES.errorLogs.detail(id), {
        params: {
          include_trace: true,
          include_request: true,
          include_user_agent: true,
        },
      })
    },
    
    // ===== 错误解决操作 =====
    
    /**
     * 标记错误为已解决
     * 
     * 使用场景：
     * - 管理员分析完错误后，标记为已解决
     * - 记录解决说明，说明如何修复的问题
     * - 用于跟踪错误的处理进度
     * 
     * @param id - 错误日志ID
     * @param data - 解决表单数据
     *   - resolution_note?: string - 解决说明（可选，最长1000字符）
     *     示例：'已修复空指针异常，在Material类的getName方法中添加了null检查'
     * 
     * @returns Promise<ErrorLog> 标记后的错误日志对象
     * 
     * 数据流向：
     * 1. 管理员查看错误详情，分析并修复问题
     * 2. 管理员点击"标记为已解决"按钮，填写解决说明
     * 3. 前端调用：errorLogApi.resolveError(123, { resolution_note: '已修复' })
     * 4. 发送请求：POST /api/v1/error-logs/123/resolve
     *    请求体：{ resolution_note: '已修复空指针异常...' }
     * 5. 后端处理：ErrorLogController@resolve 方法
     *    - 验证 ID 是否存在
     *    - 验证解决说明（最长1000字符）
     *    - 更新数据库：is_resolved=true, resolved_at=now(), resolved_by=当前用户, resolution_note=说明
     *    - 返回更新后的数据
     * 6. 前端处理：显示成功提示，更新页面显示
     * 
     * 代码示例：
     * ```typescript
     * // 标记错误为已解决（带说明）
     * const resolved = await errorLogApi.resolveError(123, {
     *   resolution_note: '已修复空指针异常，在Material类的getName方法中添加了null检查'
     * })
     * 
     * // 标记错误为已解决（不填说明）
     * const resolved2 = await errorLogApi.resolveError(456, {})
     * 
     * // 在详情页中使用
     * const handleResolve = async (id: number, note: string) => {
     *   try {
     *     const result = await errorLogApi.resolveError(id, {
     *       resolution_note: note
     *     })
     *     message.success('已标记为已解决')
     *     // 更新本地数据
     *     errorLog.value = result
     *   } catch (error) {
     *     message.error('标记失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：错误日志不存在
     * - 422：数据验证失败（说明超过1000字符）
     * - 403：无权限标记错误
     */
    resolveError(id: number, data: ResolveErrorForm) {
      return client.post<ErrorLog>(API_ROUTES.errorLogs.resolve(id), data)
    },
    
    /**
     * 批量标记错误为已解决
     * 
     * 使用场景：
     * - 管理员修复了某一类错误后，批量标记为已解决
     * - 批量处理重复出现的相同错误
     * - 清理已解决的错误，便于聚焦未解决的问题
     * 
     * @param data - 批量解决参数
     *   - ids: number[] - 错误日志ID数组（至少1个）
     *     示例：ids: [123, 456, 789]
     *   - resolution_note?: string - 解决说明（可选，最长1000字符）
     *     示例：'批量修复404错误，添加了缺失的路由'
     * 
     * @returns Promise<{ updated: number }> 批量处理结果
     *   返回的数据：
     *   {
     *     updated: number  // 成功标记的数量
     *   }
     * 
     * 数据流向：
     * 1. 管理员在列表页勾选多个错误
     * 2. 点击"批量标记为已解决"按钮，填写统一的解决说明
     * 3. 前端调用：errorLogApi.batchResolveErrors({ ids: [123, 456], resolution_note: '...' })
     * 4. 发送请求：POST /api/v1/error-logs/batch-resolve
     *    请求体：{ ids: [123, 456], resolution_note: '...' }
     * 5. 后端处理：ErrorLogController@batchResolve 方法
     *    - 验证 IDs 数组（至少1个，所有ID必须存在）
     *    - 验证解决说明
     *    - 批量更新数据库（只更新未解决的记录）
     *    - 返回成功更新的数量
     * 6. 前端处理：显示成功提示（如"已标记 5 条错误为已解决"），刷新列表
     * 
     * 代码示例：
     * ```typescript
     * // 批量标记错误为已解决
     * const result = await errorLogApi.batchResolveErrors({
     *   ids: [123, 456, 789],
     *   resolution_note: '批量修复404错误，添加了缺失的路由'
     * })
     * console.log(`已标记 ${result.updated} 条错误为已解决`)
     * 
     * // 在列表页中使用
     * const handleBatchResolve = async (selectedIds: number[], note: string) => {
     *   if (selectedIds.length === 0) {
     *     message.warning('请先选择要标记的错误')
     *     return
     *   }
     *   
     *   try {
     *     const result = await errorLogApi.batchResolveErrors({
     *       ids: selectedIds,
     *       resolution_note: note
     *     })
     *     message.success(`已标记 ${result.updated} 条错误为已解决`)
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('批量标记失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - IDs 数组为空
     *   - 某些ID不存在
     *   - 说明超过1000字符
     * - 403：无权限批量标记
     */
    batchResolveErrors(data: BatchResolveErrorForm) {
      return client.post<{ updated: number }>(API_ROUTES.errorLogs.batchResolve, data)
    },
    
    // ===== 删除操作 =====
    
    /**
     * 删除错误日志
     * 
     * 使用场景：
     * - 删除测试环境产生的错误日志
     * - 删除误报的错误日志
     * - 删除已处理且不再需要的错误日志
     * 
     * @param id - 要删除的错误日志的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：errorLogApi.deleteErrorLog(123)
     * 4. 发送请求：DELETE /api/v1/error-logs/123
     * 5. 后端处理：ErrorLogController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 删除记录（硬删除，不是软删除）
     *    - 返回成功响应
     * 6. 前端处理：显示成功提示，从列表中移除该记录
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定错误日志
     * await errorLogApi.deleteErrorLog(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除这条错误日志吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await errorLogApi.deleteErrorLog(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：错误日志不存在（可能已被删除）
     * - 403：无权限删除
     */
    deleteErrorLog(id: number) {
      return client.delete<void>(API_ROUTES.errorLogs.delete(id))
    },
    
    /**
     * 批量删除错误日志
     * 
     * 使用场景：
     * - 批量删除测试环境产生的错误日志
     * - 批量删除已解决且不再需要的错误日志
     * - 清理旧的错误日志记录
     * 
     * @param data - 批量删除参数
     *   - ids: number[] - 错误日志ID数组（至少1个）
     *     示例：ids: [123, 456, 789]
     * 
     * @returns Promise<{ deleted: number }> 批量删除结果
     *   返回的数据：
     *   {
     *     deleted: number  // 成功删除的数量
     *   }
     * 
     * 数据流向：
     * 1. 管理员在列表页勾选多个错误日志
     * 2. 点击"批量删除"按钮，确认删除
     * 3. 前端调用：errorLogApi.batchDeleteErrorLogs({ ids: [123, 456] })
     * 4. 发送请求：POST /api/v1/error-logs/batch-delete
     *    请求体：{ ids: [123, 456, 789] }
     * 5. 后端处理：ErrorLogController@batchDestroy 方法
     *    - 验证 IDs 数组（至少1个，所有ID必须存在）
     *    - 批量删除记录
     *    - 返回成功删除的数量
     * 6. 前端处理：显示成功提示，刷新列表
     * 
     * 代码示例：
     * ```typescript
     * // 批量删除错误日志
     * const result = await errorLogApi.batchDeleteErrorLogs({
     *   ids: [123, 456, 789]
     * })
     * console.log(`已删除 ${result.deleted} 条错误日志`)
     * 
     * // 在列表页中使用
     * const handleBatchDelete = async (selectedIds: number[]) => {
     *   if (selectedIds.length === 0) {
     *     message.warning('请先选择要删除的错误日志')
     *     return
     *   }
     *   
     *   const confirmed = await showConfirm(`确定要删除选中的 ${selectedIds.length} 条错误日志吗？`)
     *   if (!confirmed) return
     *   
     *   try {
     *     const result = await errorLogApi.batchDeleteErrorLogs({
     *       ids: selectedIds
     *     })
     *     message.success(`已删除 ${result.deleted} 条错误日志`)
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('批量删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - IDs 数组为空
     *   - 某些ID不存在
     * - 403：无权限批量删除
     */
    batchDeleteErrorLogs(data: BatchDeleteErrorForm) {
      return client.post<{ deleted: number }>(API_ROUTES.errorLogs.batchDelete, data)
    },
    
    /**
     * 清理旧的错误日志
     * 
     * 使用场景：
     * - 定期清理旧的错误日志，释放数据库空间
     * - 保留最近的错误日志，删除过期的
     * - 可以选择只清理已解决的错误，保留未解决的
     * 
     * @param data - 清理参数
     *   - days: number - 保留天数（1-365天）
     *     示例：days: 90（保留最近90天的日志，删除更早的）
     *   - only_resolved?: boolean - 是否只清理已解决的错误（默认true）
     *     示例：only_resolved: true（只删除已解决的，保留未解决的）
     * 
     * @returns Promise<{ deleted: number }> 清理结果
     *   返回的数据：
     *   {
     *     deleted: number  // 清理的记录数
     *   }
     * 
     * 数据流向：
     * 1. 管理员在系统设置或定时任务中配置清理策略
     * 2. 触发清理操作（手动或定时）
     * 3. 前端调用：errorLogApi.cleanupErrorLogs({ days: 90, only_resolved: true })
     * 4. 发送请求：POST /api/v1/error-logs/cleanup
     *    请求体：{ days: 90, only_resolved: true }
     * 5. 后端处理：ErrorLogController@cleanup 方法
     *    - 验证天数（1-365）
     *    - 计算截止日期（now - days）
     *    - 根据 only_resolved 参数决定是否只删除已解决的
     *    - 删除符合条件的记录
     *    - 返回删除数量
     * 6. 前端处理：显示清理结果（如"已清理 1234 条错误日志"）
     * 
     * 代码示例：
     * ```typescript
     * // 清理90天前已解决的错误日志
     * const result = await errorLogApi.cleanupErrorLogs({
     *   days: 90,
     *   only_resolved: true
     * })
     * console.log(`已清理 ${result.deleted} 条错误日志`)
     * 
     * // 清理180天前的所有错误日志（包括未解决的）
     * const result2 = await errorLogApi.cleanupErrorLogs({
     *   days: 180,
     *   only_resolved: false
     * })
     * 
     * // 在系统设置页面使用
     * const handleCleanup = async (days: number, onlyResolved: boolean) => {
     *   const confirmed = await showConfirm(
     *     `确定要清理 ${days} 天前的${onlyResolved ? '已解决' : '所有'}错误日志吗？`
     *   )
     *   if (!confirmed) return
     *   
     *   try {
     *     const result = await errorLogApi.cleanupErrorLogs({
     *       days,
     *       only_resolved: onlyResolved
     *     })
     *     message.success(`已清理 ${result.deleted} 条错误日志`)
     *   } catch (error) {
     *     message.error('清理失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - days 不在 1-365 范围内
     *   - days 不是数字
     * - 403：无权限清理错误日志
     * - 500：清理过程中数据库错误
     */
    cleanupErrorLogs(data: CleanupErrorLogsForm) {
      return client.post<{ deleted: number }>(API_ROUTES.errorLogs.cleanup, data)
    },
    
    // ===== 统计分析 =====
    
    /**
     * 获取错误日志统计数据
     * 
     * 使用场景：
     * - 在错误日志管理页面的统计看板中使用
     * - 显示错误统计摘要、按级别统计、按类型统计等
     * - 监控系统错误情况，发现异常趋势
     * - 用于生成错误报告
     * 
     * @param params - 统计查询参数（可选）
     *   - period?: 'today' | 'week' | 'month' | 'year' - 统计周期，默认：today
     *     示例：period: 'week'（统计本周的错误）
     * 
     * @returns Promise<ErrorLogStatistics> 统计数据对象
     *   返回的数据结构：
     *   {
     *     summary: {                    // 统计摘要
     *       totalErrors: number,        // 总错误数
     *       unresolvedErrors: number,   // 未解决错误数
     *       resolvedErrors: number,     // 已解决错误数
     *       resolutionRate: number      // 解决率（百分比）
     *     },
     *     errorsByLevel: [              // 按级别统计
     *       { level: 'error', levelName: '错误', count: 50 },
     *       { level: 'warning', levelName: '警告', count: 30 },
     *       ...
     *     ],
     *     errorsByType: [               // 按类型统计（TOP 10）
     *       { type: 'NotFoundHttpException', count: 20, totalOccurrences: 150 },
     *       ...
     *     ],
     *     errorsByEnvironment: [        // 按环境统计
     *       { environment: 'production', count: 80 },
     *       { environment: 'staging', count: 20 }
     *     ],
     *     frequentErrors: [             // 最频繁的错误（TOP 10）
     *       {
     *         type: 'QueryException',
     *         message: 'SQLSTATE[42S02]: Base table or view not found',
     *         occurrenceCount: 50,
     *         isResolved: false
     *       },
     *       ...
     *     ]
     *   }
     * 
     * 数据流向：
     * 1. 用户打开错误日志管理页面或统计看板
     * 2. 前端调用：errorLogApi.getErrorLogStatistics({ period: 'week' })
     * 3. 发送请求：GET /api/v1/error-logs/statistics?period=week
     * 4. 后端处理：ErrorLogController@statistics 方法
     *    - 根据 period 参数确定时间范围（今天、本周、本月、本年）
     *    - 统计总错误数、未解决错误数
     *    - 按级别分组统计
     *    - 按类型分组统计（TOP 10）
     *    - 按环境分组统计
     *    - 查询最频繁的错误（TOP 10，按 occurrence_count 排序）
     * 5. 返回数据：ErrorLogStatistics 对象
     * 6. 前端显示：在统计看板中展示数据（图表、卡片等）
     * 
     * 代码示例：
     * ```typescript
     * // 获取今天的错误统计
     * const todayStats = await errorLogApi.getErrorLogStatistics({
     *   period: 'today'
     * })
     * console.log(`今天共有 ${todayStats.summary.totalErrors} 个错误`)
     * console.log(`解决率：${todayStats.summary.resolutionRate}%`)
     * 
     * // 获取本周的错误统计
     * const weekStats = await errorLogApi.getErrorLogStatistics({
     *   period: 'week'
     * })
     * 
     * // 在统计看板中使用
     * const statsData = ref<ErrorLogStatistics | null>(null)
     * 
     * const loadStatistics = async (period: 'today' | 'week' | 'month') => {
     *   statsData.value = await errorLogApi.getErrorLogStatistics({ period })
     * }
     * 
     * // 显示按级别统计的饼图
     * const levelChartData = computed(() => {
     *   if (!statsData.value) return []
     *   return statsData.value.errorsByLevel.map(item => ({
     *     name: item.levelName,
     *     value: item.count
     *   }))
     * })
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（period 不是有效值）
     * - 500：统计查询失败
     */
    getErrorLogStatistics(params?: ErrorLogStatisticsQueryParams) {
      return client.get<ErrorLogStatistics>(API_ROUTES.errorLogs.statistics, { params })
    },
    
    /**
     * 获取错误日志趋势数据
     * 
     * 使用场景：
     * - 在错误日志管理页面的趋势图表中使用
     * - 显示错误数量随时间的变化趋势
     * - 对比已解决和未解决错误的趋势
     * - 监控系统稳定性，发现错误高峰期
     * 
     * @param params - 趋势查询参数（可选）
     *   - period?: 'today' | 'week' | 'month' | 'year' - 统计周期，默认：week
     *     示例：period: 'month'（按天统计本月的错误）
     * 
     * @returns Promise<ErrorLogTrend> 趋势数据数组
     *   返回的数据结构：
     *   [
     *     {
     *       timePeriod: '2025-10-14',  // 时间段（格式根据period不同而不同）
     *       total: 50,                 // 总错误数
     *       resolved: 30,              // 已解决数量
     *       unresolved: 20             // 未解决数量
     *     },
     *     {
     *       timePeriod: '2025-10-15',
     *       total: 45,
     *       resolved: 35,
     *       unresolved: 10
     *     },
     *     ...
     *   ]
     * 
     * 数据流向：
     * 1. 用户查看错误日志趋势图表
     * 2. 前端调用：errorLogApi.getErrorLogTrend({ period: 'month' })
     * 3. 发送请求：GET /api/v1/error-logs/trend?period=month
     * 4. 后端处理：ErrorLogController@trend 方法
     *    - 根据 period 参数确定时间范围和分组格式
     *      - today: 按小时分组（%Y-%m-%d %H:00:00）
     *      - week/month: 按天分组（%Y-%m-%d）
     *      - year: 按月分组（%Y-%m）
     *    - 查询指定时间范围内的错误日志
     *    - 按时间段分组统计（总数、已解决、未解决）
     *    - 按时间顺序排序
     * 5. 返回数据：ErrorLogTrend 数组
     * 6. 前端显示：在趋势图表中展示数据（折线图、柱状图等）
     * 
     * 代码示例：
     * ```typescript
     * // 获取本月的错误趋势（按天）
     * const monthTrend = await errorLogApi.getErrorLogTrend({
     *   period: 'month'
     * })
     * 
     * // 获取本周的错误趋势（按天）
     * const weekTrend = await errorLogApi.getErrorLogTrend({
     *   period: 'week'
     * })
     * 
     * // 在趋势图表中使用
     * const trendData = ref<ErrorLogTrend>([])
     * 
     * const loadTrend = async (period: 'week' | 'month') => {
     *   trendData.value = await errorLogApi.getErrorLogTrend({ period })
     * }
     * 
     * // 转换为图表数据格式
     * const chartData = computed(() => {
     *   return {
     *     xAxis: trendData.value.map(item => item.timePeriod),
     *     series: [
     *       {
     *         name: '总错误数',
     *         data: trendData.value.map(item => item.total)
     *       },
     *       {
     *         name: '已解决',
     *         data: trendData.value.map(item => item.resolved)
     *       },
     *       {
     *         name: '未解决',
     *         data: trendData.value.map(item => item.unresolved)
     *       }
     *     ]
     *   }
     * })
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（period 不是有效值）
     * - 500：趋势查询失败
     */
    getErrorLogTrend(params?: ErrorLogTrendQueryParams) {
      return client.get<ErrorLogTrend>(API_ROUTES.errorLogs.trend, { params })
    },
  }
}

